import { useState, useRef, useEffect } from 'react'
import { Loader } from '@googlemaps/js-api-loader'
import { useTranslation } from 'react-i18next'

// Haversine distance between two lat/lon points, in kilometres.
const distanceKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371
  const toRad = d => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/**
 * Delivery location picker: Google map with a draggable marker, Places autocomplete search, and a delivery
 * radius circle (from the branch's deliveryKmRange). Selections outside the radius are rejected, mirroring
 * the customer storefront delivery-address screen.
 *
 * Props:
 *  - onLocationSelect({ address, lat, lon }) — called on Confirm.
 *  - onClose() — called on Close.
 *  - initialLat / initialLon — marker seed (the current pickup/drop-off point).
 *  - boundaryCenter { lat, lng } / boundaryRadiusKm — branch centre + delivery range (km). Optional; when
 *    absent no circle is drawn and no boundary is enforced.
 */
const DeliveryLocationPicker = ({ onLocationSelect, onClose, initialLat, initialLon, boundaryCenter, boundaryRadiusKm }) => {
  const { t } = useTranslation()
  const mapRef = useRef(null)
  const searchRef = useRef(null)
  const markerRef = useRef(null)
  const circleRef = useRef(null)

  const [address, setAddress] = useState('')
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [outOfBounds, setOutOfBounds] = useState(false)

  const hasBoundary = Boolean(boundaryCenter) && Number(boundaryRadiusKm) > 0
  const isWithinBoundary = (lat, lon) => {
    if (!hasBoundary) return true
    return distanceKm(boundaryCenter.lat, boundaryCenter.lng, lat, lon) <= Number(boundaryRadiusKm)
  }

  const googleMapKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  useEffect(() => {
    let cancelled = false
    const loader = new Loader({ apiKey: googleMapKey, version: 'weekly', libraries: ['places'] })

    loader.load().then(() => {
      if (cancelled || !mapRef.current) return

      const hasInitial = Boolean(initialLat && initialLon)
      // Center on the existing point when editing; otherwise on the branch (or a Muscat default).
      const center = hasInitial ? { lat: initialLat, lng: initialLon } : hasBoundary ? { lat: boundaryCenter.lat, lng: boundaryCenter.lng } : { lat: 23.588, lng: 58.3829 }

      // Match the storefront delivery map: a fixed zoom of 13 centered on the point (no fit-to-circle).
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 13,
        streetViewControl: false,
        fullscreenControl: false,
      })

      // Delivery radius circle (km -> metres). Non-clickable so clicks fall through to the map.
      if (hasBoundary) {
        const circle = new window.google.maps.Circle({
          map: mapInstance,
          center: { lat: boundaryCenter.lat, lng: boundaryCenter.lng },
          radius: Number(boundaryRadiusKm) * 1000,
          strokeColor: '#2563eb',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#3b82f6',
          fillOpacity: 0.15,
          clickable: false,
        })
        circleRef.current = circle
      }

      const resolveLocation = (lat, lon) => {
        setOutOfBounds(!isWithinBoundary(lat, lon))
        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({ location: { lat, lng: lon } }, (results, status) => {
          const resolvedAddress = status === 'OK' && results[0] ? results[0].formatted_address : ''
          setAddress(resolvedAddress)
          setSelectedLocation({ lat, lon, address: resolvedAddress })
        })
      }

      const placeMarker = position => {
        if (markerRef.current) markerRef.current.setMap(null)
        const marker = new window.google.maps.Marker({ position, map: mapInstance, draggable: true })
        markerRef.current = marker
        marker.addListener('dragend', e => resolveLocation(e.latLng.lat(), e.latLng.lng()))
      }

      if (initialLat && initialLon) {
        placeMarker({ lat: initialLat, lng: initialLon })
        resolveLocation(initialLat, initialLon)
      }

      const autocomplete = new window.google.maps.places.Autocomplete(searchRef.current, {
        componentRestrictions: { country: 'om' },
        fields: ['geometry', 'formatted_address', 'name'],
      })
      autocomplete.bindTo('bounds', mapInstance)
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        if (!place.geometry || !place.geometry.location) return
        mapInstance.setCenter(place.geometry.location)
        mapInstance.setZoom(14)
        placeMarker(place.geometry.location)
        const lat = place.geometry.location.lat()
        const lon = place.geometry.location.lng()
        setOutOfBounds(!isWithinBoundary(lat, lon))
        const newAddress = place.formatted_address || ''
        setAddress(newAddress)
        setSelectedLocation({ lat, lon, address: newAddress })
      })

      mapInstance.addListener('click', event => {
        placeMarker(event.latLng)
        resolveLocation(event.latLng.lat(), event.latLng.lng())
      })

      // The modal opens with a scale/opacity transition; once it settles, relayout the map so tiles fill
      // the container (a map created mid-transition can otherwise render grey/partial).
      setTimeout(() => {
        if (cancelled) return
        window.google.maps.event.trigger(mapInstance, 'resize')
        mapInstance.setCenter(center)
      }, 350)
    })

    return () => {
      cancelled = true
    }
  }, [])

  const confirmDisabled = !selectedLocation || (hasBoundary && outOfBounds)
  const handleConfirmLocation = () => {
    if (!confirmDisabled) onLocationSelect(selectedLocation)
  }

  return (
    <div className="w-full" style={{ height: '520px', display: 'flex', flexDirection: 'column' }}>
      <input
        type="text"
        ref={searchRef}
        className="w-full p-2 mb-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-dark-500"
        placeholder={t('common.placeholders.searchLocation') || 'Search location'}
      />
      <div ref={mapRef} className="w-full rounded-lg" style={{ flex: 1, minHeight: '340px' }} />

      {hasBoundary && (
        <div className="mt-2 text-xs text-gray-500">
          {outOfBounds ? (
            <span className="text-red-600">Selected location is outside the {Number(boundaryRadiusKm)} km delivery range. Pick a point inside the circle.</span>
          ) : (
            <span>Delivery available within {Number(boundaryRadiusKm)} km of the branch (shaded circle).</span>
          )}
        </div>
      )}
      {address && <div className="mt-1 text-sm text-gray-700 truncate">{address}</div>}

      <div className="mt-3 w-full flex justify-between">
        <button type="button" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none" onClick={onClose}>
          Close
        </button>
        <button
          type="button"
          className="px-4 py-2 bg-dark-600 text-white rounded-md hover:bg-dark-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleConfirmLocation}
          disabled={confirmDisabled}
        >
          Confirm Location
        </button>
      </div>
    </div>
  )
}

export default DeliveryLocationPicker
